import React, { useEffect, useState } from 'react';
import styled from 'styled-components';
import { FiEye, FiEyeOff } from 'react-icons/fi';
import { getOrCreateDeviceId } from '../utils/deviceId';
import TotpSetupQrBlock from './TotpSetupQrBlock';

const Wrapper = styled.div`
  min-height: 100vh;
  display: flex;
  align-items: center;
  justify-content: center;
  background: linear-gradient(135deg, #2193b0 0%, #6dd5ed 100%);
`;

const FormBox = styled.div`
  background: #fff;
  border-radius: 18px;
  box-shadow: 0 8px 32px #2193b044;
  padding: 38px 32px 32px 32px;
  min-width: 340px;
  max-width: 380px;
  width: 100%;
`;

const Title = styled.h2`
  text-align: center;
  font-size: 2.1em;
  font-weight: 800;
  color: #2193b0;
  margin-bottom: 18px;
`;

const Input = styled.input`
  width: 100%;
  padding: 12px 14px;
  margin-bottom: 16px;
  border-radius: 8px;
  border: 1px solid #b2bec3;
  font-size: 1em;
  background: #f8f9fa;
  transition: border 0.2s;
  &:focus {
    border: 1.5px solid #2193b0;
    outline: none;
  }
`;

const Button = styled.button`
  width: 100%;
  padding: 12px;
  background: linear-gradient(90deg, #43e97b 0%, #38f9d7 100%);
  color: #fff;
  font-size: 1.1em;
  font-weight: 700;
  border: none;
  border-radius: 8px;
  cursor: pointer;
  margin-bottom: 8px;
  transition: background 0.2s;
  &:hover {
    background: linear-gradient(90deg, #2193b0 0%, #6dd5ed 100%);
  }
`;

const Link = styled.span`
  display: block;
  text-align: center;
  color: #2193b0;
  cursor: pointer;
  margin-top: 10px;
  font-size: 0.98em;
  text-decoration: underline;
`;

const AvatarPreview = styled.img`
  display: block;
  margin: 0 auto 16px auto;
  width: 72px;
  height: 72px;
  border-radius: 50%;
  object-fit: cover;
  box-shadow: 0 2px 12px #2193b044;
`;

const PasswordWrapper = styled.div`
  position: relative;
  width: 100%;
  margin-bottom: 16px;
`;

const PasswordInput = styled(Input)`
  padding-right: 45px;
`;

const EyeIcon = styled.button`
  position: absolute;
  right: 12px;
  top: 50%;
  transform: translateY(-50%);
  background: none;
  border: none;
  cursor: pointer;
  color: #666;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 4px;
  transition: color 0.2s;
  
  &:hover {
    color: #2193b0;
  }
  
  &:focus {
    outline: none;
  }
`;

const TrustRow = styled.label`
  display: flex;
  align-items: center;
  gap: 10px;
  margin-bottom: 14px;
  font-size: 0.95em;
  color: #2d3436;
  cursor: pointer;
  user-select: none;
`;

export default function AuthForm() {
  const [mode, setMode] = useState('login');
  const [loginStep, setLoginStep] = useState('password');
  const [preAuthToken, setPreAuthToken] = useState('');
  const [totpCode, setTotpCode] = useState('');
  const [setupSecret, setSetupSecret] = useState('');
  const [setupOtpAuthUrl, setSetupOtpAuthUrl] = useState('');
  const [setupCode, setSetupCode] = useState('');
  const [setupLoading, setSetupLoading] = useState(false);
  const [trustDevice, setTrustDevice] = useState(true);
  const [form, setForm] = useState({
    first_name: '',
    last_name: '',
    password: '',
    birth_day: '',
    birth_month: '',
    birth_year: '',
    avatar: null,
    department: ''
  });
  const [avatarPreview, setAvatarPreview] = useState(null);
  const [avatarInputKey, setAvatarInputKey] = useState(0);
  const [error, setError] = useState('');
  const [registerSuccess, setRegisterSuccess] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [registrationEnabled, setRegistrationEnabled] = useState(true);

  useEffect(() => {
    let mounted = true;
    const load = async () => {
      try {
        const r = await fetch('/api/registration-settings');
        const data = await r.json().catch(() => ({}));
        if (!mounted || !r.ok) return;
        setRegistrationEnabled(data.registrationEnabled !== false);
      } catch (_) {
        // В случае ошибки не блокируем вход и оставляем регистрацию в прежнем режиме UI.
      }
    };
    load();
    return () => { mounted = false; };
  }, []);

  useEffect(() => {
    if (!registrationEnabled && mode === 'register') {
      setMode('login');
      setError('Самостоятельная регистрация отключена. Обратитесь к HR или администратору.');
      setRegisterSuccess('');
    }
  }, [registrationEnabled, mode]);

  const persistSession = (data) => {
    localStorage.setItem('token', data.token);
    if (data.refreshToken) {
      localStorage.setItem('refreshToken', data.refreshToken);
    }
    const userObj = {
      ...data.user,
      username: data.user.username || `${data.user.first_name || ''} ${data.user.last_name || ''}`.trim()
    };
    localStorage.setItem('user', JSON.stringify(userObj));
    window.location.reload();
  };

  const handleChange = e => {
    const { name, value, files } = e.target;
    if (name === 'avatar' && files && files[0]) {
      setForm(f => ({ ...f, avatar: files[0] }));
      setAvatarPreview(URL.createObjectURL(files[0]));
    } else {
      setForm(f => ({ ...f, [name]: value }));
    }
  };

  const handleSubmit = async e => {
    e.preventDefault();
    setError('');
    setRegisterSuccess('');
    if (mode === 'login') {
      const deviceId = getOrCreateDeviceId();
      if (loginStep === '2faSetup') {
        if (!setupCode.trim() || !setupSecret) {
          setError('Откройте приложение-аутентификатор и введите код.');
          return;
        }
        try {
          const res = await fetch('/api/login/2fa/enable', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
              preAuthToken,
              secret: setupSecret,
              code: setupCode.replace(/\s/g, ''),
              deviceId,
              trustDevice
            })
          });
          const data = await res.json();
          if (res.status === 403 && data.error === 'ACCOUNT_PENDING_APPROVAL') {
            setError('Аккаунт ожидает одобрения администратором или HR. После одобрения вы сможете войти.');
            return;
          }
          if (!res.ok) throw new Error(data.error || 'Ошибка подтверждения 2FA');
          persistSession(data);
        } catch (err) {
          console.error('Ошибка обязательной настройки 2FA:', err);
          setError(err.message);
        }
        return;
      }
      if (loginStep === '2fa') {
        if (!totpCode.trim()) {
          setError('Введите код из приложения-аутентификатора');
          return;
        }
        try {
          const res = await fetch('/api/login/2fa', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
              preAuthToken,
              code: totpCode.replace(/\s/g, ''),
              deviceId,
              trustDevice
            })
          });
          const data = await res.json();
          if (res.status === 403 && data.error === 'ACCOUNT_PENDING_APPROVAL') {
            setError('Аккаунт ожидает одобрения администратором или HR. После одобрения вы сможете войти.');
            return;
          }
          if (!res.ok) throw new Error(data.error || 'Ошибка авторизации');
          persistSession(data);
        } catch (err) {
          console.error('Ошибка 2FA:', err);
          setError(err.message);
        }
        return;
      }

      if (!form.first_name || !form.last_name || !form.password) {
        setError('Заполните все поля!');
        return;
      }
      try {
        const res = await fetch('/api/login', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            first_name: form.first_name,
            last_name: form.last_name,
            password: form.password,
            deviceId,
            trustDevice
          })
        });
        const data = await res.json();
        if (res.status === 403 && data.error === 'ACCOUNT_PENDING_APPROVAL') {
          setError('Аккаунт ожидает одобрения администратором или HR. После одобрения вы сможете войти.');
          return;
        }
        if (!res.ok) throw new Error(data.error || 'Ошибка авторизации');
        if (data.requires2faSetup && data.preAuthToken) {
          setPreAuthToken(data.preAuthToken);
          setLoginStep('2faSetup');
          setTotpCode('');
          setSetupCode('');
          setError('');
          setSetupLoading(true);
          try {
            const setupResp = await fetch('/api/login/2fa/setup', {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify({ preAuthToken: data.preAuthToken })
            });
            const setupData = await setupResp.json().catch(() => ({}));
            if (!setupResp.ok) throw new Error(setupData.error || 'Не удалось подготовить настройку 2FA');
            setSetupSecret(setupData.secret || '');
            setSetupOtpAuthUrl(setupData.otpauthUrl || '');
          } catch (setupErr) {
            console.error('Ошибка получения секрета 2FA:', setupErr);
            setError(setupErr.message || 'Ошибка настройки 2FA');
          } finally {
            setSetupLoading(false);
          }
          return;
        }
        if (data.requires2fa && data.preAuthToken) {
          setPreAuthToken(data.preAuthToken);
          setLoginStep('2fa');
          setTotpCode('');
          setError('');
          return;
        }
        persistSession(data);
      } catch (err) {
        console.error('Ошибка входа:', err);
        setError(err.message);
      }
    } else {
      if (!form.first_name || !form.last_name || !form.password || !form.birth_day || !form.birth_month || !form.birth_year) {
        setError('Заполните все обязательные поля!');
        return;
      }
      try {
        // Сначала регистрируем пользователя
        const res = await fetch('/api/register', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            first_name: form.first_name,
            last_name: form.last_name,
            password: form.password,
            birth_day: form.birth_day,
            birth_month: form.birth_month,
            birth_year: form.birth_year,
            department: form.department
          })
        });
        const data = await res.json();
        if (!res.ok) {
          if (data?.error === 'REGISTRATION_DISABLED') {
            throw new Error(data?.message || 'Самостоятельная регистрация отключена');
          }
          throw new Error(data.error || 'Ошибка регистрации');
        }
        if (data.pendingApproval) {
          setRegisterSuccess(
            data.message ||
              'Заявка отправлена. После одобрения администратором или HR вы сможете войти в приложение.'
          );
          setForm((f) => ({ ...f, password: '', avatar: null }));
          setAvatarPreview(null);
          setAvatarInputKey((k) => k + 1);
          setMode('login');
          return;
        }
        localStorage.setItem('token', data.token);
        if (data.refreshToken) {
          localStorage.setItem('refreshToken', data.refreshToken);
        }
        const userObj = {
          ...data.user,
          username: data.user.username || `${data.user.first_name || ''} ${data.user.last_name || ''}`.trim()
        };
        localStorage.setItem('user', JSON.stringify(userObj));
        // После успешной регистрации загружаем аватар (только если сразу выдан доступ)
        let avatarUrl = data.user.avatarUrl || data.user.avatar_url || '';
        if (form.avatar) {
          const fd = new FormData();
          fd.append('avatar', form.avatar);
          const token = data.token;
          const resAvatar = await fetch('/api/upload-avatar', {
            method: 'POST',
            headers: { Authorization: `Bearer ${token}` },
            body: fd
          });
          const avatarData = await resAvatar.json();
          if (!resAvatar.ok) throw new Error(avatarData.error || 'Ошибка загрузки фото');
          avatarUrl = avatarData.avatarUrl || avatarData.avatar_url || '';
          // Обновляем пользователя в localStorage с правильным форматом (avatarUrl в camelCase)
          const user = { 
            ...data.user, 
            avatarUrl: avatarUrl,
            avatar_url: avatarUrl // Для обратной совместимости
          };
          localStorage.setItem('user', JSON.stringify(user));
        }
        window.location.reload();
      } catch (err) {
        // Выводим подробности ошибки в консоль браузера
        console.error('Ошибка авторизации:', err);
        setError(err.message);
      }
    }
  };

  return (
    <Wrapper>
      <FormBox>
        <Title>Добро пожаловать в команду Issa Plus</Title>
        <form onSubmit={handleSubmit}>
          {mode === 'login' && loginStep === '2fa' ? (
            <>
              <p style={{ textAlign: 'center', color: '#2d3436', marginBottom: 14, fontSize: '0.98em' }}>
                Введите одноразовый код из Google Authenticator.
                Если приложение ещё не привязано — откройте мессенджер на компьютере → кнопка 2FA у фото профиля → отсканируйте QR.
              </p>
              <Input
                name="totp"
                inputMode="numeric"
                autoComplete="one-time-code"
                placeholder="Код 2FA"
                value={totpCode}
                onChange={(e) => setTotpCode(e.target.value)}
                autoFocus
              />
              <TrustRow>
                <input type="checkbox" checked={trustDevice} onChange={(e) => setTrustDevice(e.target.checked)} />
                Запомнить это устройство (~30 дней)
              </TrustRow>
            </>
          ) : mode === 'login' && loginStep === '2faSetup' ? (
            <>
              <p style={{ textAlign: 'center', color: '#2d3436', marginBottom: 10, fontSize: '0.96em', lineHeight: 1.45 }}>
                Для безопасности аккаунта сначала нужно подключить двухфакторный вход.
              </p>
              {setupLoading ? (
                <p style={{ textAlign: 'center', color: '#2193b0', marginBottom: 12 }}>Подготовка секрета 2FA…</p>
              ) : (
                <>
                  <div style={{ fontSize: '0.9em', color: '#2d3436', marginBottom: 8 }}>
                    1) Отсканируйте QR в Google Authenticator (или введите ключ вручную):
                  </div>
                  <TotpSetupQrBlock variant="light" value={setupOtpAuthUrl} size={220} />
                  {setupOtpAuthUrl ? (
                    <div style={{ fontSize: '0.9em', marginTop: -6, marginBottom: 12, textAlign: 'center' }}>
                      <a
                        href={setupOtpAuthUrl}
                        style={{ color: '#2193b0', wordBreak: 'break-all' }}
                        onClick={(e) => e.stopPropagation()}
                      >
                        Открыть ссылку otpauth (если Authenticator на этом телефоне)
                      </a>
                    </div>
                  ) : null}
                  <Input
                    name="setupSecret"
                    value={setupSecret}
                    readOnly
                    onFocus={(e) => e.target.select()}
                    placeholder="Секрет 2FA"
                  />
                  <div style={{ fontSize: '0.9em', color: '#2d3436', marginBottom: 8 }}>
                    2) Введите текущий код из приложения:
                  </div>
                  <Input
                    name="setupCode"
                    inputMode="numeric"
                    autoComplete="one-time-code"
                    placeholder="Код подтверждения 2FA"
                    value={setupCode}
                    onChange={(e) => setSetupCode(e.target.value)}
                    autoFocus
                  />
                </>
              )}
              <TrustRow>
                <input type="checkbox" checked={trustDevice} onChange={(e) => setTrustDevice(e.target.checked)} />
                Запомнить это устройство (~30 дней)
              </TrustRow>
            </>
          ) : (
            <>
              <Input name="first_name" placeholder="Имя" value={form.first_name} onChange={handleChange} autoFocus required />
              <Input name="last_name" placeholder="Фамилия" value={form.last_name} onChange={handleChange} required />
              <PasswordWrapper>
                <PasswordInput
                  name="password"
                  type={showPassword ? 'text' : 'password'}
                  placeholder="Пароль"
                  value={form.password}
                  onChange={handleChange}
                  required
                />
                <EyeIcon
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  aria-label={showPassword ? 'Скрыть пароль' : 'Показать пароль'}
                >
                  {showPassword ? <FiEyeOff size={20} /> : <FiEye size={20} />}
                </EyeIcon>
              </PasswordWrapper>
              {mode === 'login' && (
                <TrustRow>
                  <input type="checkbox" checked={trustDevice} onChange={(e) => setTrustDevice(e.target.checked)} />
                  Запомнить это устройство после входа
                </TrustRow>
              )}
            </>
          )}
          {mode === 'register' && (
            <>
              <Input name="birth_day" type="number" min="1" max="31" placeholder="День рождения" value={form.birth_day} onChange={handleChange} required />
              <Input name="birth_month" type="number" min="1" max="12" placeholder="Месяц рождения" value={form.birth_month} onChange={handleChange} required />
              <Input name="birth_year" type="number" min="1900" max="2026" placeholder="Год рождения" value={form.birth_year} onChange={handleChange} required />
              <select name="department" value={form.department} onChange={handleChange} required style={{ width:'100%', padding:'12px 14px', marginBottom:16, borderRadius:8, border:'1px solid #b2bec3', background:'#f8f9fa' }}>
                <option value="">Выберите должность</option>
                <option>Организатор фотосессий</option>
                <option>Супервайзер колл-центра</option>
                <option>Менеджер отдела возвратов</option>
                <option>Менеджер склада</option>
                <option>Оператор колл-центра</option>
                <option>Менеджер отдела продаж</option>
                <option>Финансовый менеджер</option>
                <option>ОТК (отдел технического контроля)</option>
                <option>SMM-менеджер</option>
                <option>Водитель</option>
                <option>Менеджер по дропшиппингу</option>
                <option>Менеджер по качеству обслуживания клиентов</option>
                <option>Менеджер маркетплейса</option>
                <option>Бухгалтер по первичной документации</option>
                <option>Руководитель отдела закупок</option>
                <option>Супервайзер отдела возвратов</option>
                <option>CEO (генеральный директор)</option>
                <option>Контент-менеджер</option>
                <option>Завсклад (заведующий складом)</option>
                <option>Системный администратор</option>
                <option>Владелец компании</option>
                <option>Помощник SMM-менеджера</option>
                <option>Руководитель отдела логистики</option>
                <option>HR-менеджер (менеджер по персоналу)</option>
                <option>Менеджер отдела маркетинга</option>
                <option>Менеджер отдела закупок</option>
                <option>CFO (финансовый директор)</option>
                <option>Оператор-учётчик</option>
              </select>
              <Input
                key={avatarInputKey}
                name="avatar"
                type="file"
                accept=".png,.jpg,.jpeg"
                onChange={handleChange}
              />
              <div style={{ fontSize: '0.88em', color: '#636e72', marginTop: -8, marginBottom: 12, textAlign: 'center' }}>
                Фото необязательно; при модерации заявки его можно будет загрузить в профиле после первого входа.
              </div>
              {avatarPreview && <AvatarPreview src={avatarPreview} alt="avatar" />}
            </>
          )}
          {registerSuccess && (
            <div style={{ color: '#27ae60', marginBottom: 10, textAlign: 'center', fontSize: '0.98em', lineHeight: 1.45 }}>
              {registerSuccess}
            </div>
          )}
          {error && <div style={{color:'#e74c3c',marginBottom:8,textAlign:'center'}}>{error}</div>}
          <Button type="submit">
            {mode === 'login' && (loginStep === '2fa' || loginStep === '2faSetup') ? 'Подтвердить' : mode === 'login' ? 'Войти' : 'Зарегистрироваться'}
          </Button>
          {mode === 'login' && (loginStep === '2fa' || loginStep === '2faSetup') && (
            <button
              type="button"
              onClick={() => {
                setLoginStep('password');
                setPreAuthToken('');
                setTotpCode('');
                setSetupSecret('');
                setSetupOtpAuthUrl('');
                setSetupCode('');
                setError('');
              }}
              style={{
                display: 'block',
                width: '100%',
                marginTop: 10,
                textAlign: 'center',
                color: '#2193b0',
                cursor: 'pointer',
                fontSize: '0.98em',
                textDecoration: 'underline',
                background: 'none',
                border: 'none',
                font: 'inherit'
              }}
            >
              Назад к паролю
            </button>
          )}
        </form>
        {(registrationEnabled || mode === 'register') && (
          <Link
            onClick={() => {
              if (!registrationEnabled && mode === 'login') return;
              setMode(mode === 'login' ? 'register' : 'login');
              setLoginStep('password');
              setPreAuthToken('');
              setTotpCode('');
              setError('');
              setRegisterSuccess('');
            }}
          >
            {mode === 'login' ? 'Нет аккаунта? Зарегистрироваться' : 'Уже есть аккаунт? Войти'}
          </Link>
        )}
      </FormBox>
    </Wrapper>
  );
}
